/**
 * Tanjong Life · workflow 01 · the self-account question, asked of the Claims Evidence
 * Analyst — which is NOT autonomous, and says so.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `claims-evidence-analyst`. The answer is
 * deliberately smaller than a sentinel's, and it is not an apology. Three memories, two of
 * them desk standards somebody installed and one learned from two named claims and written up
 * by a person. That is the whole list, and the demo point is that the difference between this
 * agent and the Completeness Sentinel beside it is a governance choice somebody made on
 * purpose and can point at on the page.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveEvidence: Scenario = {
  id: 'tanjong-chat-improve-evidence',
  orgId: 'tanjong-life',
  agentPk: 'claims-evidence-analyst',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Once, on 24 March, and I did not do it by myself. Two files were assembled against the current wording and came out looking complete while missing the item the wording in force on the event date actually required. Somebody wrote that up as a lesson, somebody approved it, and since then I establish the event date first and assemble second. My other two memories are desk standards that were installed in me — they are not things I noticed.',
      sources: [
        'Memory · learned from two 2026 claims · 24 March 2026',
        'Memory · claims handling standard · installed',
      ],
      delay: 1200,
    },
    {
      do: 'insight',
      title: 'What I do not do between files',
      body: 'I do not carry anything from one file to the next on my own. Nothing I noticed this morning leans on this afternoon, and no probe of mine watches what happened after I handed the work on. That is not an omission — it is why my working can be reconstructed months later by somebody who was not here. An agent that quietly accumulated could give two identical files two different answers and neither of us could say why.',
      sources: ['Runtime · Wand-native, not autonomous', 'Memory · lessons only, written by a person'],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'My evaluation is 78, up ten, and the ten came from the lesson a person wrote. The number I am worst at is applicable-version awareness, at 38 — establishing which wording an item is being assembled against. I did not find that either; the suite found it, and it will keep finding it until somebody decides to act on it. The Completeness Sentinel on this workflow would have opened a case about it against itself. I cannot, and that is the difference between us.',
      sources: ['Evaluation · 78 overall · Applicable-version awareness 38'],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open Completeness Sentinel — the one that can',
      href: '/agents/completeness-sentinel',
      delay: 600,
    },
  ],
};
